import React, { Component } from 'react';
import PropTypes from 'prop-types';
import 'react-responsive-carousel/lib/styles/carousel.min.css'; // requires a loader
import { Carousel } from 'react-responsive-carousel';
import {
  Button,
  IconButton,
  Dialog,
  DialogContent,
  DialogActions,
  DialogTitle,
  TextField,
  InputAdornment,
  ClickAwayListener,
  Checkbox,
  FormControlLabel,
  Icon,
  Chip,
} from '@material-ui/core';

import {
  NavigateNext, NavigateBefore, Visibility, VisibilityOff, Lock, LockOpen, Clear, Search, ArrowDropDown, ArrowDropUp,
} from '@material-ui/icons';
import { DateTimePicker, MuiPickersUtilsProvider } from 'material-ui-pickers';
import MomentUtils from '@date-io/moment';
import moment from 'moment';
import 'moment/locale/de';

import { LoadingIndicator, DropDownMenu } from '..';
import { caseInsensitiveMatch } from '../../utils/case-insensitive-match';


const TreeItemsIndex = {
  menus: 0,
  categories: 1,
  menuEntries: 2,
};

const TreeItems = {
  0: 'menus',
  1: 'categories',
  2: 'menuEntries',
};

class ProductDialog extends Component {
  constructor(props) {
    super(props);
    this.state = {
      openMenus: [],
      openCategories: [],
      currentViewIndex: 0,
      currentItem: {},
      askForProductUpdate: false,
      notifyAboutProductUpdate: false,
      options: {},
      searchItem: 'menuEntries',
      selectedItems: [],
      searchFocused: false,
      searchTerm: '',
      itemAssignments: {},
    };

    this.carouselRef = React.createRef();
  }

  componentDidUpdate(prevProps) {
    const { productDialogOpen, user, loadMenuTree, loadProducts } = this.props;
    const { productDialogOpen: wasOpen } = prevProps;

    if (productDialogOpen && !wasOpen) {
      const locationId = user.staffLocationID;
      loadMenuTree(locationId);
      loadProducts(locationId);
    }
  }

  get views() {
    return [
      { name: 'Menüs', value: 'menus', parent: '' },
      { name: 'Kategorien', value: 'categories', parent: 'Menü' },
      { name: 'Menüeinträge', value: 'menuEntries', parent: 'Kategorie' },
    ];
  }

  get searchItems() {
    return [
      { name: 'Menüs', value: 'menus' },
      { name: 'Kategorien', value: 'categories' },
      { name: 'Menüeinträge', value: 'menuEntries' },
      { name: 'Produkte', value: 'products' },
    ];
  }

  get assignmentItems() {
    return [
      { name: 'Nicht zugewiesen', value: -1 },
      { name: 'Ja', value: true },
      { name: 'Nein', value: false },
    ];
  }

  get searchResults() {
    const { menus, products } = this.props;
    const { searchTerm, searchItem } = this.state;

    const nameMatch = caseInsensitiveMatch.bind(null, searchTerm);

    if (searchItem === 'products') {
      return products.filter(p => nameMatch(p.name));
    }

    const filteredMenus = [];
    menus.forEach((menu) => {
      if (searchItem === 'menus') {
        if (nameMatch(menu.name)) {
          filteredMenus.push(menu);
        }
        return;
      }

      const filteredCategories = [];
      menu.categories.forEach((category) => {
        if (searchItem === 'categories') {
          if (nameMatch(category.name)) {
            filteredCategories.push(category);
          }
          return;
        }

        const filteredMenuEntries = [];
        category.menuEntries.forEach((menuEntry) => {
          const product = menuEntry.productId || menuEntry.productId === 0 ? products.find(p => p.productID === menuEntry.productId) : null;
          const visibleFromProduct = product && !product.isVisible;
          const orderableFromProduct = product && product.isLocked;
          const productName = product ? product.name : '';
          const entry = {
            ...menuEntry,
            name: menuEntry.name || productName,
            visible: visibleFromProduct ? product.isVisible : menuEntry.visible,
            visibleFromProduct,
            orderable: orderableFromProduct ? !product.isLocked : menuEntry.orderable,
            orderableFromProduct,
            product,
          };
          if (nameMatch(entry.name)) {
            filteredMenuEntries.push(entry);
          }
        });

        if (filteredMenuEntries.length) {
          filteredCategories.push({ ...category, filteredMenuEntries });
        }
      });

      if (filteredCategories.length) {
        filteredMenus.push({ ...menu, filteredCategories });
      }
    });

    return filteredMenus;
  }

  get isSearchView() {
    const { searchFocused, searchTerm } = this.state;
    return searchFocused && !!searchTerm;
  }

  get showMultipleActions() {
    const { multipleActionsOpen, selectedItems } = this.state;
    return multipleActionsOpen && selectedItems.length > 1;
  }

  closeDialog = () => {
    const { toggleProductDialog } = this.props;
    toggleProductDialog(false);
  }

  stepBack = () => {
    const { currentViewIndex, currentItem } = this.state;
    const { menus } = this.props;
    const nextViewIndex = currentViewIndex - 1;
    const nextItem = nextViewIndex === 0 ? {} : menus.find(m => m.categories.find(c => c.id === currentItem.id));
    this.setState({ currentViewIndex: nextViewIndex, currentItem: nextItem, searchTerm: '', selectedItems: [] });
  }

  stepForward = (currentItem) => {
    const { currentViewIndex, searchItem } = this.state;

    let nextViewIndex = currentViewIndex + 1;
    if (this.isSearchView) {
      nextViewIndex = searchItem === 'menus' ? 1 : 2;
    }

    this.setState({ currentViewIndex: nextViewIndex, currentItem, searchTerm: '', searchFocused: false, selectedItems: [] });
  }

  updateMenu = (menu, prop, value) => {
    const { updateMenu } = this.props;
    updateMenu(menu.id, {
      visible: menu.visibleInApp,
      orderable: menu.orderable,
      [prop]: value,
    });
  }

  updateCategory = (category, prop, value) => {
    const { updateCategory } = this.props;
    updateCategory(category.id, {
      visible: category.visible,
      orderable: category.orderable,
      [prop]: value,
    });
  }

  updateMenuEntry = (menuEntry, prop, value) => {
    const { updateMenuEntry } = this.props;

    const { orderableFromProduct, visibleFromProduct, product } = menuEntry;

    const currentInfoFromMenuEntry = (prop === 'orderable' && !orderableFromProduct) || (prop === 'visible' && !visibleFromProduct);
    const showNotification = !!product && (!currentInfoFromMenuEntry || !value);

    if (showNotification) {
      const options = { menuEntry, prop, value };
      if (currentInfoFromMenuEntry) {
        // prop currently true and switched to false > ask if product should be changed
        this.setState({
          askForProductUpdate: true,
          options,
        });
      } else {
        // prop currently false and info is from product > can only switch product prop > notify
        this.setState({
          notifyAboutProductUpdate: true,
          options,
        });
      }
      return;
    }

    updateMenuEntry(menuEntry.id, {
      visible: menuEntry.visible,
      orderable: menuEntry.orderable,
      [prop]: value,
    });
  }

  updateProduct = (product, prop, value) => {
    const { updateProduct } = this.props;
    updateProduct(product.productID, { [prop]: value });
  }

  updateMultipleItems = () => {
    const { itemAssignments, selectedItems, searchItem, multipleActionsOpen, currentViewIndex } = this.state;
    const { updateMenus, updateCategories, updateMenuEntries, updateProducts } = this.props;

    const data = {};

    const propIsSet = prop => ![undefined, null, -1].includes(itemAssignments[prop]);

    const setDataProp = (prop) => {
      if (propIsSet(prop)) {
        data[prop] = itemAssignments[prop];
      }
    };

    const setHoursDataProp = (prop, hoursProp) => {
      if (propIsSet(prop)) {
        data[hoursProp] = {
          days: 255,
          exclude: true,
          endDate: itemAssignments[prop].valueOf(),
          startTime: '00:00:00',
          endTime: '23:59:59',
        };
      }
    };

    setDataProp('orderable');
    setDataProp('visible');
    setHoursDataProp('lockedUntil', 'orderableHours');
    setHoursDataProp('invisibleUntil', 'visibleHours');

    const currentItems = this.isSearchView ? searchItem : TreeItems[currentViewIndex];

    const ids = currentItems === 'products' ? selectedItems.map(i => i.productID) : selectedItems.map(i => i.id);

    switch (currentItems) {
      case 'menus':
        updateMenus(ids, data);
        this.resetMultiActions();
        break;
      case 'categories':
        updateCategories(ids, data);
        this.resetMultiActions();
        break;
      case 'menuEntries':
        if ((data.orderable && selectedItems.find(i => i.orderableFromProduct)) || (data.visible && selectedItems.find(i => i.visibleFromProduct))) {
          // at least one menuentry is locked / invisible because of its product
          this.setState({ askForProductsUpdate: true, options: data });
          return;
        }
        updateMenuEntries(ids, data);
        this.resetMultiActions();
        break;
      case 'products':
        updateProducts(ids, data);
        this.resetMultiActions();
        break;
      default:
        this.resetMultiActions();
        break;
    }
  }

  resetMultiActions = () => {
    this.setState({ multipleActionsOpen: false, itemAssignments: {}, selectedItems: [], askForProductsUpdate: false });
  }

  filterByTerm = (item) => {
    const { searchTerm } = this.state;
    const nameMatch = caseInsensitiveMatch.bind(null, searchTerm);
    return !searchTerm || nameMatch(item.name);
  }

  renderItemContent = (item, updateItem, options = {}) => {
    const { visibleProp = 'visible' } = options;
    const { isLoadingProducts } = this.props;
    const { currentViewIndex, searchItem, selectedItems } = this.state;

    const selected = !!selectedItems.find(i => i.id === item.id);
    const forwardDisabled = (this.isSearchView && (searchItem === 'products' || searchItem === 'menuEntries')) || currentViewIndex === TreeItemsIndex.menuEntries || (currentViewIndex === TreeItemsIndex.categories && isLoadingProducts);

    return (
      <>
        <div className="item-content">
          <FormControlLabel
            control={
              <Checkbox
                color="primary"
                name={`checked-${item.id}`}
                checked={selected}
                onChange={() => {
                  if (selected) {
                    this.setState({ selectedItems: selectedItems.filter(i => i.id !== item.id) });
                    return;
                  }
                  this.setState({ selectedItems: [item, ...selectedItems] });
                }}
              />
            }
          />
          <div
            className="name"
            onClick={() => {
              if (!forwardDisabled) {
                this.stepForward(item);
              }
            }}
          >{item.name}</div>
          <div className="actions">
            <IconButton classes={{ root: 'orderable' }} onClick={() => updateItem(item, 'orderable', !item.orderable)}>
              {item.orderable ? <LockOpen /> : <Lock />}
            </IconButton>
            <IconButton classes={{ root: 'visible' }} onClick={() => updateItem(item, 'visible', !item[visibleProp])}>
              {item[visibleProp] ? <Visibility /> : <VisibilityOff />}
            </IconButton>
          </div>
        </div>
        <IconButton disabled={forwardDisabled} classes={{ root: `select-item ${forwardDisabled ? 'disabled' : ''}` }} onClick={() => this.stepForward(item)}>
          <NavigateNext />
        </IconButton>
      </>
    );
  }

  renderMenuEntries = () => {
    const { menus, products } = this.props;
    const { currentItem, currentViewIndex } = this.state;

    if (currentViewIndex !== TreeItemsIndex.menuEntries) {
      return null;
    }

    const menu = menus.find(m => m.categories.find(c => c.id === currentItem.id));
    const category = menu.categories.find(c => c.id === currentItem.id);

    let someProducts = false;

    return (
      <div className="menuEntries">
        {!category.menuEntries.length && <div className="menuEntry">Keine Menüeinträge vorhanden!</div>}
        {category.menuEntries.map((menuEntry) => {
          const product = menuEntry.productId || menuEntry.productId === 0 ? products.find(p => p.productID === menuEntry.productId) : null;
          const visibleFromProduct = product && !product.isVisible;
          const orderableFromProduct = product && product.isLocked;
          const productName = product ? product.name : '';
          const entry = {
            ...menuEntry,
            name: menuEntry.name || productName,
            visible: visibleFromProduct ? product.isVisible : menuEntry.visible,
            visibleFromProduct,
            orderable: orderableFromProduct ? !product.isLocked : menuEntry.orderable,
            orderableFromProduct,
            product,
          };

          if (!this.filterByTerm(entry)) {
            return null;
          }

          if (product) {
            someProducts = true;
          }

          return (
            <div className={`menuEntry ${product ? 'with-product' : ''}`} key={menuEntry.id}>
              {this.renderItemContent(entry, this.updateMenuEntry)}
            </div>
          );
        })}
        {someProducts && (
          <div className="product-hint">
            <div className="color-box" />
            <div>Produkt hinterlegt</div>
          </div>
        )}
      </div>
    );
  }

  renderCategories = () => {
    const { menus } = this.props;
    const { currentItem, currentViewIndex } = this.state;

    if (currentViewIndex !== TreeItemsIndex.categories) {
      return null;
    }

    const menu = menus.find(m => m.id === currentItem.id);

    return (
      <div className="categories">
        {!menu.categories.length && <div className="category">Keine Kategorien vorhanden!</div>}
        {menu.categories.filter(this.filterByTerm).map(category => (
          <div className="category" key={category.id}>
            {this.renderItemContent(category, this.updateCategory)}
          </div>
        ))}
      </div>
    );
  }

  renderMenus = () => {
    const { menus } = this.props;
    const { currentViewIndex } = this.state;
    if (currentViewIndex !== TreeItemsIndex.menus) {
      return null;
    }

    return (
      <div className="menus">
        {menus.filter(this.filterByTerm).map(menu => (
          <div className="menu" key={menu.id}>
            {this.renderItemContent(menu, this.updateMenu, { visibleProp: 'visibleInApp' })}
          </div>
        ))}
      </div>
    );
  }

  renderSearchResults() {
    const { searchItem } = this.state;
    const { searchResults } = this;

    if (searchItem === 'products') {
      return (
        <div key="product-result">
          <div className="current-view">Produkte</div>
          {searchResults.map(product => (
            <div className="product" key={product.productID}>
              {this.renderItemContent({ ...product, id: product.productID, orderable: !product.isLocked, visible: product.isVisible }, this.updateProduct)}
            </div>
          ))}
        </div>
      );
    }

    let someProducts = false;

    return (
      <div key="tree-results">
        {searchResults.map((menu) => {
          if (searchItem === 'menus') {
            return (
              <div className="menu" key={menu.id}>
                {this.renderItemContent(menu, this.updateMenu, { visibleProp: 'visibleInApp' })}
              </div>
            );
          }

          return (
            <div>
              {searchItem === 'categories' && <div className="current-view">{menu.name}</div>}
              {menu.filteredCategories.map((category) => {
                if (searchItem === 'categories') {
                  return (
                    <div className="category" key={category.id}>
                      {this.renderItemContent(category, this.updateCategory)}
                    </div>
                  );
                }

                return (
                  <div>
                    <div className="current-view">{`${menu.name} > ${category.name}`}</div>
                    {category.filteredMenuEntries.map((menuEntry) => {
                      if (!someProducts && !!menuEntry.product) {
                        someProducts = true;
                      }
                      return (
                        <div className={`menuEntry ${menuEntry.product ? 'with-product' : ''}`} key={menuEntry.id}>
                          {this.renderItemContent(menuEntry, this.updateMenuEntry)}
                        </div>
                      );
                    })}
                  </div>
                );
              })}
            </div>
          );
        })}
        {someProducts && (
          <div className="product-hint">
            <div className="color-box" />
            <div>Produkt hinterlegt</div>
          </div>
        )}
      </div>
    );
  }

  renderMultiActionsView() {
    const { itemAssignments, selectedItems } = this.state;
    const buttonsDisabled = [undefined, -1].includes(itemAssignments.visible) && [undefined, -1].includes(itemAssignments.orderable);

    return (
      <div className="multiple-actions-content">
        <div className="multiple-actions-headline">Ausgewählte Einträge</div>
        <div className="selected-items">
          {selectedItems.map(item => (
            <Chip
              classes={{ root: 'selected-item' }}
              color="secondary"
              label={item.name}
              key={item.id}
              onDelete={() => {
                const newItems = selectedItems.filter(i => i.id !== item.id);
                this.setState({
                  selectedItems: newItems,
                  multipleActionsOpen: newItems.length,
                });
              }}
            />
          ))}
        </div>
        <div className="multiple-actions-headline">Optionen</div>
        <div>
          <div className={`multiple-option ${itemAssignments.orderable === false ? 'with-datepicker' : ''}`}>
            <DropDownMenu
              label="Bestellbar"
              formControl="dropdown-formcontrol"
              items={this.assignmentItems}
              value={itemAssignments.orderable}
              onChange={(e, value, key) => this.setState({ itemAssignments: { ...itemAssignments, orderable: key } })}
            />
            {itemAssignments.orderable === false && (
              <div className="datepicker-wrapper">
                <MuiPickersUtilsProvider utils={MomentUtils} moment={moment}>
                  <DateTimePicker
                    label="Deaktiviert bis ..."
                    onChange={dateTime => this.setState({ itemAssignments: { ...itemAssignments, lockedUntil: dateTime } })}
                    value={itemAssignments.lockedUntil || null}
                    cancelLabel="Abbrechen"
                    format="DD.MM.YYYY HH:mm"
                    ampm={false}
                    disablePast
                    fullWidth
                  />
                </MuiPickersUtilsProvider>
                {itemAssignments.lockedUntil && (
                  <IconButton
                    onClick={() => this.setState({ itemAssignments: { ...itemAssignments, lockedUntil: null } })}
                  >
                    <Clear />
                  </IconButton>
                )}
              </div>
            )}
          </div>
          <div className={`multiple-option ${itemAssignments.visible === false ? 'with-datepicker' : ''}`}>
            <DropDownMenu
              label="Sichtbar"
              formControl="dropdown-formcontrol"
              items={this.assignmentItems}
              value={itemAssignments.visible}
              onChange={(e, value, key) => this.setState({ itemAssignments: { ...itemAssignments, visible: key } })}
            />
            {itemAssignments.visible === false && (
              <div className="datepicker-wrapper">
                <MuiPickersUtilsProvider utils={MomentUtils} moment={moment}>
                  <DateTimePicker
                    label="Deaktiviert bis ..."
                    onChange={dateTime => this.setState({ itemAssignments: { ...itemAssignments, invisibleUntil: dateTime } })}
                    value={itemAssignments.invisibleUntil || null}
                    cancelLabel="Abbrechen"
                    format="DD.MM.YYYY HH:mm"
                    ampm={false}
                    disablePast
                    fullWidth
                  />
                </MuiPickersUtilsProvider>
                {itemAssignments.invisibleUntil && (
                  <IconButton
                    onClick={() => this.setState({ itemAssignments: { ...itemAssignments, invisibleUntil: null } })}
                  >
                    <Clear />
                  </IconButton>
                )}
              </div>
            )}
          </div>
        </div>
        <div className="multiple-actions-buttons">
          <Button
            disabled={buttonsDisabled}
            onClick={() => this.setState({ itemAssignments: {} })}
          >
            Leeren
          </Button>
          <Button
            color="primary"
            variant="contained"
            disabled={buttonsDisabled}
            onClick={this.updateMultipleItems}
          >
            Zuweisen
          </Button>
        </div>
      </div>
    );
  }

  renderMultiAssignment() {
    const { selectedItems, multipleActionsOpen } = this.state;

    return (
      <div className="multiple-actions-toggle">
        <Button
          onClick={() => this.setState({ multipleActionsOpen: !multipleActionsOpen, itemAssignments: {} })}
          disabled={!selectedItems.length}
          color="secondary"
        >
          Mehrfachzuweisung <Icon>{multipleActionsOpen ? <ArrowDropUp /> : <ArrowDropDown />}</Icon>
        </Button>
      </div>
    );
  }

  renderSearchView() {
    const { searchResults } = this;

    if (!searchResults.length) {
      return (
        <div className="no-entries">Keine Ergebnisse gefunden!</div>
      );
    }

    return (
      <div className="search-results">
        {this.renderSearchResults()}
      </div>
    );
  }

  renderContent() {
    const { isLoadingMenus } = this.props;
    if (isLoadingMenus) {
      return <div className="justify-center"><LoadingIndicator /></div>;
    }

    const { currentViewIndex, multipleActionsOpen } = this.state;

    if (multipleActionsOpen) {
      return this.renderMultiActionsView();
    }

    if (this.isSearchView) {
      return this.renderSearchView();
    }

    const view = this.views[currentViewIndex];

    return (
      <div className="menu-tree">
        <div className="current-view">{view.name}</div>
        <div className="entries">
          <Carousel
            showArrows={false}
            showStatus={false}
            showThumbs={false}
            showIndicators={false}
            useKeyboardArrows={false}
            infiniteLoop={false}
            autoPlay={false}
            transitionTime={250}
            swipeable={false}
            selectedItem={currentViewIndex}
            ref={this.carouselRef}
          >
            {this.renderMenus()}
            {this.renderCategories()}
            {this.renderMenuEntries()}
          </Carousel>
        </div>
      </div>
    );
  }

  renderSearch = () => {
    const { searchFocused, searchTerm, searchItem, currentViewIndex } = this.state;
    const { isLoadingMenus, isLoadingProducts } = this.props;

    const searchDisabled = isLoadingMenus || isLoadingProducts;
    const isFocused = searchFocused || currentViewIndex === TreeItemsIndex.menus;

    return (
      <ClickAwayListener
        onClickAway={(e) => {
          const isSearchItemField = e.target.nodeName === 'BODY';
          if (!searchTerm && !isSearchItemField) {
            this.setState({ searchFocused: false });
          }
        }}
      >
        <div className="search">
          <IconButton
            onClick={() => {
              if (!searchFocused) {
                this.setState({ searchFocused: true, selectedItems: [] });
              }
            }}
            classes={{ root: 'search-icon' }}
          >
            <Search />
          </IconButton>
          <div className="search-text-field-wrapper">
            {isFocused && (
              <>
                <DropDownMenu
                  label="Suchen nach ..."
                  formControl="dropdown-formcontrol"
                  items={this.searchItems}
                  value={searchItem}
                  onChange={(e, value, key) => this.setState({ searchItem: key, selectedItems: [] })}
                />
                <TextField
                  fullWidth
                  label="Suchbegriff"
                  classes={{ root: 'search-text-field' }}
                  InputProps={{
                    classes: { input: 'text-field-input' },
                    endAdornment: searchTerm ? (
                      <InputAdornment position="end">
                        <IconButton
                          onClick={() => this.setState({ searchTerm: '', searchFocused: false, selectedItems: [] })}
                        >
                          <Clear />
                        </IconButton>
                      </InputAdornment>
                    ) : null,
                  }}
                  value={searchTerm}
                  autoFocus
                  onChange={e => this.setState({ searchTerm: e.target.value, selectedItems: [], searchFocused: true })}
                  onFocus={() => {
                    const input = document.getElementsByClassName('text-field-input')[0];
                    if (input) {
                      input.select();
                    }
                  }}
                  disabled={searchDisabled}
                />
              </>
            )}
          </div>
        </div>
      </ClickAwayListener>
    );
  }

  renderDialogTitle = () => {
    const { currentViewIndex, currentItem, multipleActionsOpen } = this.state;

    if (multipleActionsOpen) {
      return (
        <div className="current-item-info">
          <IconButton onClick={() => this.setState({ multipleActionsOpen: false, itemAssignments: {} })}>
            <NavigateBefore />
          </IconButton>
          <div className="name">Mehrfachzuweisung</div>
        </div>
      );
    }

    if (currentViewIndex === TreeItemsIndex.menus) {
      return 'Einträge sperren';
    }

    const view = this.views[currentViewIndex];

    return (
      <div className="current-item-info">
        <IconButton onClick={this.stepBack}>
          <NavigateBefore />
        </IconButton>
        <div className="name">{`${view.parent}: ${currentItem.name}`}</div>
      </div>
    );
  }

  renderAskForProductsUpdateDialog() {
    const { updateMenuEntries, updateProducts } = this.props;
    const { askForProductsUpdate, options, selectedItems } = this.state;

    if (!askForProductsUpdate) {
      return null;
    }

    const menuEntryIds = selectedItems.map(i => i.id);
    const menuEntriesWithProductProp = selectedItems.filter(i => (options.orderable && i.orderableFromProduct) || (options.visible && i.visibleFromProduct));
    const affectedProducts = menuEntriesWithProductProp.map(m => m.product).filter((p, i, s) => s.indexOf(p) === i);
    const affectedProductsNames = affectedProducts.map(p => p.name);

    let unlockType = '';
    if (options.orderable && options.visible) {
      unlockType = 'Sichtbar und Bestellbar';
    } else if (options.orderable) {
      unlockType = 'Bestellbar';
    } else if (options.visible) {
      unlockType = 'Sichtbar';
    }

    return (
      <Dialog
        open={askForProductsUpdate}
        classes={{ root: 'product-update-dialog' }}
      >
        <DialogTitle>{`Produkt${affectedProducts.length > 1 ? 'e' : ''} verknüpft`}</DialogTitle>
        <DialogContent>
          <div>{`${affectedProducts.length > 1 ? 'Folgende Produkte sind' : 'Folgendes Produkt ist'} bei einem oder mehreren der Menüeinträge hinterlegt:`}</div>
          <div className="product-name">{affectedProductsNames.join(', ')}</div>
          <div>{`Damit das ${unlockType} schalten bei den betroffenen Menüeinträgen greift, ${affectedProducts.length > 1 ? 'müssen auch die entsprechenden Produkte' : 'muss auch das entsprechende Produkt'} angepasst werden. Soll die Produktanpassung gemacht werden?`}</div>
          <div className="actions">
            <Button
              color="primary"
              onClick={() => {
                updateMenuEntries(menuEntryIds, options);
                this.resetMultiActions();
              }}
              variant="contained"
            >
              Nein
            </Button>
            <Button
              color="primary"
              variant="contained"
              onClick={() => {
                updateMenuEntries(menuEntryIds, options);
                const productIds = affectedProducts.map(p => p.productID);
                updateProducts(productIds, options);
                this.resetMultiActions();
              }}
            >
              Ja
            </Button>
          </div>
        </DialogContent>
        <DialogActions>
          <Button color="primary" onClick={() => this.setState({ askForProductsUpdate: false })}>Schließen</Button>
        </DialogActions>
      </Dialog>
    );
  }

  renderAskForProductUpdateDialog() {
    const { updateMenuEntry } = this.props;
    const { askForProductUpdate, options } = this.state;
    const { menuEntry, prop, value } = options;
    const product = menuEntry && menuEntry.product ? menuEntry.product : {};
    return (
      <Dialog
        open={askForProductUpdate}
        classes={{ root: 'product-update-dialog' }}
      >
        <DialogTitle>Produkt verknüpft</DialogTitle>
        <DialogContent>
          <div>{`Der Menüeintrag ${menuEntry ? menuEntry.name : ''} hat folgendes Produkt zugewiesen:`}</div>
          <div className="product-name">{product.name}</div>
          <div>Sind von der Aktualisierung alle Einträge, die auf dieses Produkt referenzieren betroffen, oder nur dieser Menüeintrag?</div>
          <div className="actions">
            <Button
              color="primary"
              onClick={() => {
                updateMenuEntry(menuEntry.id, { [prop]: value });
                this.setState({ askForProductUpdate: false });
              }}
              variant="contained"
            >
              nur Menüeintrag
            </Button>
            <Button
              color="primary"
              variant="contained"
              onClick={() => {
                updateMenuEntry(menuEntry.id, { [prop]: value });
                this.updateProduct(product, prop, value);
                this.setState({ askForProductUpdate: false });
              }}
            >
              alle
            </Button>
          </div>
        </DialogContent>
        <DialogActions>
          <Button color="primary" onClick={() => this.setState({ askForProductUpdate: false })}>Schließen</Button>
        </DialogActions>
      </Dialog>
    );
  }

  renderNotifyAboutProductUpdateDialog() {
    const { updateMenuEntry } = this.props;
    const { notifyAboutProductUpdate, options } = this.state;
    const { menuEntry, prop, value } = options;
    const product = menuEntry && menuEntry.product ? menuEntry.product : {};
    return (
      <Dialog
        open={notifyAboutProductUpdate}
        classes={{ root: 'product-update-dialog' }}
      >
        <DialogTitle>Produkt verknüpft</DialogTitle>
        <DialogContent>
          <div>{`Die Sperre vom Menüeintrag ${menuEntry ? menuEntry.name : ''} kommt von folgendem Produkt:`}</div>
          <div className="product-name">{product.name}</div>
          <div>Mit dem Fortfahren wird die Sperre vom Produkt aufgehoben und betrifft somit auch alle anderen Menüeinträge, die dieses Produkt zugewiesen haben.</div>
        </DialogContent>
        <DialogActions>
          <Button color="primary" onClick={() => this.setState({ notifyAboutProductUpdate: false })}>Schließen</Button>
          <Button
            color="primary"
            onClick={() => {
              this.updateProduct(product, prop, value);
              updateMenuEntry(menuEntry.id, { [prop]: value });
              this.setState({ notifyAboutProductUpdate: false });
            }}
          >
            Fortfahren
          </Button>
        </DialogActions>
      </Dialog>
    );
  }

  renderDialogTitleContent() {
    const { searchFocused, currentViewIndex, multipleActionsOpen } = this.state;

    const withSearchHeader = (searchFocused || currentViewIndex === TreeItemsIndex.menus) && !multipleActionsOpen;

    return (
      <div className={`dialog-title-content ${withSearchHeader ? 'focused-search' : ''}`}>
        {this.renderDialogTitle()}
        {!multipleActionsOpen && this.renderSearch()}
      </div>
    );
  }

  render() {
    const { productDialogOpen } = this.props;
    const { multipleActionsOpen } = this.state;

    return (
      <>
        <Dialog
          open={productDialogOpen}
          aria-labelledby="alert-dialog-title"
          aria-describedby="alert-dialog-description"
          fullWidth
          classes={{ root: 'product-dialog' }}
        >
          <DialogTitle classes={{ root: 'dialog-title' }}>
            {this.renderDialogTitleContent()}
          </DialogTitle>
          <DialogContent>
            {this.renderContent()}
          </DialogContent>
          <DialogActions classes={{ root: 'dialog-actions' }}>
            {this.renderMultiAssignment()}
            <Button color="primary" onClick={this.closeDialog} disabled={multipleActionsOpen}>Schließen</Button>
          </DialogActions>
        </Dialog>
        {this.renderAskForProductUpdateDialog()}
        {this.renderNotifyAboutProductUpdateDialog()}
        {this.renderAskForProductsUpdateDialog()}
      </>
    );
  }
}

ProductDialog.propTypes = {
  productDialogOpen: PropTypes.bool,
  isLoadingMenus: PropTypes.bool,
  menus: PropTypes.array,
  user: PropTypes.object,
  products: PropTypes.array,
  isLoadingProducts: PropTypes.bool,
  loadProducts: PropTypes.func,
  toggleProductDialog: PropTypes.func,
  loadMenuTree: PropTypes.func,
  updateMenu: PropTypes.func,
  updateCategory: PropTypes.func,
  updateMenuEntry: PropTypes.func,
  updateProduct: PropTypes.func,
  updateMenus: PropTypes.func,
  updateCategories: PropTypes.func,
  updateMenuEntries: PropTypes.func,
  updateProducts: PropTypes.func,
};

ProductDialog.defaultProps = {
  productDialogOpen: false,
  isLoadingMenus: false,
  menus: [],
  user: {},
  products: [],
  isLoadingProducts: false,
  loadProducts: () => {},
  toggleProductDialog: () => {},
  loadMenuTree: () => {},
  updateMenu: () => {},
  updateCategory: () => {},
  updateMenuEntry: () => {},
  updateProduct: () => {},
  updateMenus: () => {},
  updateCategories: () => {},
  updateMenuEntries: () => {},
  updateProducts: () => {},
};

export default ProductDialog;
